import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Target, Heart, Zap, Trophy, ArrowRight, Mail, Phone, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SocialMediaSidebar from '@/components/SocialMediaSidebar';
import HeroVideo from '@/components/HeroVideo';
import ZoneCard from '@/components/ZoneCard';
import TimelineItem from '@/components/TimelineItem';
import RegistrationForm from '@/components/RegistrationForm';
import Footer from '../components/Footer';
import CyberpunkValuesGrid from '@/components/CyberpunkValuesGrid';

const HomePage = () => {
  const { i18n, t } = useTranslation();

  // About section data
  const values = [
    {
      icon: Users,
      title: 'Community First',
      description: 'Building a vibrant community of tech enthusiasts, makers, and innovators across Oman.',
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'Promoting cutting-edge technology and inspiring the next generation of creators.',
    },
    {
      icon: Heart,
      title: 'Accessibility',
      description: 'Making advanced technology approachable and fun for people of all ages and backgrounds.',
    },
    {
      icon: Zap,
      title: 'Excellence',
      description: 'Delivering world-class experiences through professional organization and safety standards.',
    },
  ];

  // Event zones data
  const zones = [
    {
      title: 'FPV Drone Racing Championship',
      description: 'Experience the thrill of high-speed FPV drone racing with professional pilots competing on a challenging track with gates, obstacles, and time trials.',
      image: 'https://images.unsplash.com/photo-1565632655969-2423fed2ef4a?q=80&w=800&auto=format&fit=crop',
      icon: Zap
    },
    {
      title: 'Robo Soccer Arena',
      description: 'Watch autonomous robots compete in exciting soccer matches with real-time strategy, precise ball control, and intense competition.',
      image: 'https://images.unsplash.com/photo-1603286656381-6f04fb84435e?q=80&w=800&auto=format&fit=crop',
      icon: Target
    },
    {
      title: 'Experience Zone: You Crash It, We Fix It',
      description: 'Try flying tiny whoop drones yourself in our safe crash zone, plus test your skills on professional flight simulators. Perfect for beginners!',
      image: 'https://images.unsplash.com/photo-1728632286888-04c64f48e506?q=80&w=800&auto=format&fit=crop',
      icon: Users
    },
    {
      title: 'Robo Battle Arena',
      description: 'Watch combat robots clash in intense battles with bracket-style tournaments, safety barriers, and professional judging.',
      image: 'https://images.unsplash.com/photo-1760629863094-5b1e8d1aae74?q=80&w=800&auto=format&fit=crop',
      icon: Trophy
    },
  ];

  // Schedule data
  const [selectedDay, setSelectedDay] = React.useState('day1');
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const scheduleData = {
    day1: [
      { time: '6:00 PM - 10:00 PM', title: 'Drone Soccer Arena', description: 'All-time activity throughout the evening', category: 'experience' },
      { time: '6:00 PM - 10:00 PM', title: 'Drone Simulator Zone', description: 'All-time activity throughout the evening', category: 'experience' },
      { time: '6:00 PM - 10:00 PM', title: 'Experience Zone: "You Crash It, We Fix It"', description: 'All-time activity throughout the evening', category: 'experience' },
    ],
    day2: [
      { time: '8:00 PM - 9:00 PM', title: 'Robo Battle Championship', description: 'Scheduled highlight event', category: 'robotics' },
      { time: '6:00 PM - 10:00 PM', title: 'Drone Soccer Arena', description: 'All-time activity throughout the evening', category: 'experience' },
      { time: '6:00 PM - 10:00 PM', title: 'Drone Simulator Zone', description: 'All-time activity throughout the evening', category: 'experience' },
      { time: '6:00 PM - 10:00 PM', title: 'Experience Zone', description: 'All-time activity throughout the evening', category: 'experience' },
    ],
    day3: [
      { time: '6:30 PM - 7:30 PM', title: 'Robo Battle Finals', description: 'Scheduled highlight event', category: 'robotics' },
      { time: '8:00 PM - 9:00 PM', title: 'Professional FPV Drone Race & Freestyle Show', description: 'Scheduled highlight event', category: 'racing' },
      { time: '6:00 PM - 10:00 PM', title: 'Drone Simulator Zone', description: 'All-time activity throughout the evening', category: 'experience' },
      { time: '6:00 PM - 10:00 PM', title: 'Experience Zone', description: 'All-time activity throughout the evening', category: 'experience' },
    ],
  };

  const filteredSchedule = scheduleData[selectedDay].filter(
    (item) => selectedCategory === 'all' || item.category === selectedCategory
  );

  return (
    <>
      <SocialMediaSidebar />

      {/* Hero Section with Video Background */}
      <section id="home" className="min-h-screen">
        <HeroVideo>
          <div className="flex items-center justify-center h-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-flex items-center space-x-2 px-4 py-2 border border-primary/30 bg-black/30 backdrop-blur-sm mb-8">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm font-mono uppercase tracking-widest text-primary">January 29-31, 2026 • Muscat Nights</span>
                  <span className="text-muted-foreground">•</span>
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="text-sm font-mono uppercase tracking-widest text-primary">Muscat, Oman</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl md:text-6xl lg:text-7xl font-heading font-black tracking-tighter uppercase mb-8"
              >
                <span className="bg-gradient-to-r from-white via-primary to-secondary bg-clip-text text-transparent drop-shadow-2xl">
                  Oman Drone Carnival
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-15"
              >
                <a
                  href="#register"
                  className="inline-block px-6 py-2 bg-primary text-background hover:bg-primary/90 font-bold text-sm uppercase tracking-widest transition-all duration-300 neon-glow-strong"
                >
                  {t('registerNow')}
                </a>
                <a
                  href="#schedule"
                  className="inline-block px-6 py-2 border border-primary/50 text-primary hover:bg-primary/10 font-bold text-sm uppercase tracking-widest transition-all duration-300"
                >
                  View Schedule
                </a>
              </motion.div>
            </div>
          </div>
        </HeroVideo>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="text-center mb-24"
      >
        <h2 className="text-4xl md:text-6xl font-heading font-black tracking-tighter uppercase mb-6">
          <span className="bg-gradient-to-r from-white via-primary to-secondary bg-clip-text text-transparent">
            About the Carnival
          </span>
        </h2>
        
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
          Inspiring innovation, celebrating technology, and bringing the community together.
        </p>

      
      </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="glass-effect border border-primary/10 p-12 md:p-13 mb-16"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold mb-6">Our Mission</h3>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
              Oman Drone Carnival exists to inspire youth, families, and creators by celebrating the incredible possibilities of drones and robotics. We believe technology should be exciting, accessible, and educational.
            </p>
          </motion.div>

        <CyberpunkValuesGrid values={values} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Audience cards */}
        </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'Students', description: 'Schools and colleges exploring STEM education' },
              { title: 'Families', description: 'Parents and kids looking for educational fun' },
              { title: 'Makers', description: 'Hobbyists and creators in the tech community' },
              { title: 'Professionals', description: 'Industry experts and competitive pilots' },
            ].map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-effect border border-primary/10 hover:border-primary/30 transition-all duration-300 p-8 text-center"
              >
                <h3 className="text-lg md:text-xl font-heading font-bold mb-3 text-primary">{audience.title}</h3>
                <p className="text-sm md:text-base text-muted-foreground">{audience.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Zones Section */}
      <section id="events" className="py-24 bg-muted/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-heading font-black tracking-tighter uppercase mb-6">
              <span className="bg-gradient-to-r from-white via-primary to-secondary bg-clip-text text-transparent">
                Event Zones
              </span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Four exciting zones packed with action, competition, and hands-on experiences.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {zones.map((zone, index) => (
              <ZoneCard key={index} {...zone} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-heading font-black tracking-tighter uppercase mb-6">
              <span className="bg-gradient-to-r from-white via-primary to-secondary bg-clip-text text-transparent">
                Event Schedule
              </span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Plan your visit with our three-day evening schedule.
            </p>
            <p className="text-base md:text-lg text-primary font-mono mb-2">
              January 29-31, 2026 • Muscat, Oman
            </p>
            <p className="text-sm md:text-base text-accent font-mono">
              Event Time: 6:00 PM - 10:00 PM (All Days)
            </p>
          </motion.div>

          <div className="mb-12 space-y-6">
            {/* Day Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { value: 'day1', label: 'January 29' },
                { value: 'day2', label: 'January 30' },
                { value: 'day3', label: 'January 31' },
              ].map((day) => (
                <button
                  key={day.value}
                  onClick={() => setSelectedDay(day.value)}
                  className={`px-6 md:px-8 py-3 font-bold uppercase tracking-widest transition-all duration-300 text-sm md:text-base ${
                    selectedDay === day.value
                      ? 'bg-primary text-background neon-glow'
                      : 'border border-primary/30 text-primary hover:bg-primary/10'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { value: 'all', label: 'All Events' },
                { value: 'racing', label: 'Racing' },
                { value: 'robotics', label: 'Robotics' },
                { value: 'experience', label: 'Experience' },
              ].map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 md:px-6 py-2 text-xs md:text-sm font-medium uppercase tracking-wider transition-all duration-300 ${
                    selectedCategory === cat.value
                      ? 'bg-primary/10 text-primary border border-primary'
                      : 'border border-primary/20 text-muted-foreground hover:text-primary hover:border-primary/40'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <motion.div
            key={`${selectedDay}-${selectedCategory}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            {filteredSchedule.map((item, index) => (
              <TimelineItem
                key={index}
                {...item}
                isLast={index === filteredSchedule.length - 1}
                delay={index * 0.05}
              />
            ))}
          </motion.div>

          {filteredSchedule.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-base md:text-lg">No events found for this filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Registration Section */}
      <section id="register" className="py-24 bg-muted/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-heading font-black tracking-tighter uppercase mb-6">
              <span className="bg-gradient-to-r from-white via-primary to-secondary bg-clip-text text-transparent">
                Register Now
              </span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Secure your spot at Oman Drone Carnival 2026.
            </p>
          </motion.div>

          <RegistrationForm />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-heading font-black tracking-tighter uppercase mb-6">
              <span className="bg-gradient-to-r from-white via-primary to-secondary bg-clip-text text-transparent">
                Contact Us
              </span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Have questions? We're here to help. Get in touch with our team.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            {/* Companies Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Sky Seven */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="glass-effect border border-primary/10 p-8 md:p-12"
              >
                <div className="flex flex-col items-center justify-center space-y-4 mb-6">
                  <div className="relative h-32 md:h-40 w-auto flex items-center justify-center">
                    <img 
                      src="/assets/skyseven.png" 
                      alt="Sky Seven" 
                      className="h-full w-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                      onError={(e) => {
                        console.error('Sky Seven logo image failed to load');
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Organizer
                  </span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 border border-primary/20 bg-primary/5">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground mb-1">Email</p>
                      <a
                        href="mailto:info@skiesseven.com"
                        className="text-sm md:text-base text-muted-foreground hover:text-primary transition-colors"
                      >
                        info@skiesseven.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="p-3 border border-primary/20 bg-primary/5">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground mb-1">Phone</p>
                      <p className="text-sm md:text-base text-muted-foreground">+968 9693 9694</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* White Rocks LLC */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="glass-effect border border-primary/10 p-8 md:p-12"
              >
                <div className="flex flex-col items-center justify-center space-y-4 mb-6">
                  <div className="relative h-32 md:h-40 w-auto flex items-center justify-center">
                    <img 
                      src="/assets/whiterock.png" 
                      alt="White Rocks LLC" 
                      className="h-full w-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                      onError={(e) => {
                        console.error('White Rocks logo image failed to load');
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Execution Partner
                  </span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 border border-primary/20 bg-primary/5">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground mb-1">Email</p>
                      <a
                        href="mailto:info@whiterocks.llc"
                        className="text-sm md:text-base text-muted-foreground hover:text-primary transition-colors"
                      >
                        info@whiterocks.llc
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="p-3 border border-primary/20 bg-primary/5">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground mb-1">Phone</p>
                      <p className="text-sm md:text-base text-muted-foreground">+968 77633786</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* General Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-effect border border-primary/10 p-8 md:p-12"
            >
              <h3 className="text-xl md:text-2xl font-heading font-bold mb-8 text-center">Event Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 border border-primary/20 bg-primary/5">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground mb-1">Location</p>
                    <a 
                      href="https://maps.app.goo.gl/oytYMTSFhEOEeilpG" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm md:text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      Muscat Nights, Sultanate of Oman
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 border border-primary/20 bg-primary/5">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground mb-1">Event Hours</p>
                    <p className="text-sm md:text-base text-muted-foreground">6:00 PM - 10:00 PM (All Days)</p>
                  </div>
                </div>
              </div>

              {/* Map embed */}
              <div className="mt-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14699.437583542726!2d58.44444!3d23.58611!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDM1JzEwLjAiTiA1OMKwMjYnNDAuMCJF!5e0!3m2!1sen!2s!4v1234567890!5m2!1sen!2s"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="border border-primary/10"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </>
  );
};

export default HomePage;
